import React, { useState } from 'react';
import { Typography, Button, Card, Divider } from 'antd';
import { InboxOutlined, DollarCircleOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { ReceiptDetailInlineMobile } from './ReceiptDetailInlineMobile';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { TransactionItem } from './TransactionHistory';
import './TransactionHistoryMobile.css';

interface TransactionHistoryMobileProps {
  token: string;
  password: string;
  transactions: TransactionItem[];
}

const typeMeta: Record<'receipt' | 'payment', { label: string; color: string; icon: React.ReactNode }> = {
  receipt: {
    label: 'Nhập hàng',
    color: '#0ea5e9',
    icon: <InboxOutlined />,
  },
  payment: {
    label: 'Thanh toán',
    color: '#16a34a',
    icon: <DollarCircleOutlined />,
  },
};

const TransactionHistoryMobile: React.FC<TransactionHistoryMobileProps> = ({ token, password, transactions }) => {
  const [expandedReceiptIds, setExpandedReceiptIds] = useState<Set<number>>(new Set());

  return (
    <>
      <div className="transaction-mobile-list">
        {transactions.length === 0 ? (
          <Card className="transaction-mobile-card">
            <Typography.Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              Không có giao dịch nào
            </Typography.Text>
          </Card>
        ) : (
          transactions.map((tx) => {
            const meta = typeMeta[tx.type];
            const isPositive = tx.amount >= 0;
            
            return (
              <React.Fragment key={tx.id}>
                <Card className="transaction-mobile-card" hoverable={!!tx.receiptId}>
                  <div className="transaction-mobile-card__header">
                    <div className="transaction-mobile-card__type">
                      <div
                        className="transaction-mobile-card__icon"
                        style={{ background: `${meta.color}20`, color: meta.color }}
                      >
                        {meta.icon}
                      </div>
                      <div className="transaction-mobile-card__type-info">
                        <Typography.Text strong style={{ fontSize: 16 }}>
                          {meta.label}
                        </Typography.Text>
                        {tx.type === 'receipt' && tx.status && (
                          <Typography.Text
                            type="secondary"
                            style={{ display: 'block', fontSize: 13, marginTop: 2 }}
                          >
                            {tx.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                          </Typography.Text>
                        )}
                      </div>
                    </div>
                    <div className="transaction-mobile-card__amount">
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 18,
                          color: isPositive ? '#0f172a' : '#16a34a',
                        }}
                      >
                        {isPositive ? '+' : ''}
                        {formatCurrency(Math.abs(tx.amount))}
                      </Typography.Text>
                    </div>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  <div className="transaction-mobile-card__body">
                    <div className="transaction-mobile-card__row">
                      <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                        Mã chứng từ:
                      </Typography.Text>
                      <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                        {tx.code || '—'}
                      </Typography.Text>
                    </div>
                    <div className="transaction-mobile-card__row">
                      <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                        Ngày:
                      </Typography.Text>
                      <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                        {tx.date ? formatDate(tx.date) : '—'}
                      </Typography.Text>
                    </div>
                    {tx.description && (
                      <div className="transaction-mobile-card__row">
                        <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                          Diễn giải:
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                          {tx.description}
                        </Typography.Text>
                      </div>
                    )}
                    <div className="transaction-mobile-card__row">
                      <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                        Dư nợ lũy kế:
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 14 }}>
                        {formatCurrency(tx.runningBalance || 0)}
                      </Typography.Text>
                    </div>
                  </div>

                  {tx.receiptId && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <Button
                        type="link"
                        className="transaction-mobile-card__detail-button"
                        onClick={() => {
                          const newExpanded = new Set(expandedReceiptIds);
                          if (newExpanded.has(tx.receiptId!)) {
                            newExpanded.delete(tx.receiptId!);
                          } else {
                            newExpanded.add(tx.receiptId!);
                          }
                          setExpandedReceiptIds(newExpanded);
                        }}
                        icon={expandedReceiptIds.has(tx.receiptId!) ? <UpOutlined /> : <DownOutlined />}
                      >
                        {expandedReceiptIds.has(tx.receiptId!) ? 'Ẩn chi tiết' : 'Xem chi tiết'}
                      </Button>
                    </>
                  )}
                </Card>
                {tx.receiptId && expandedReceiptIds.has(tx.receiptId) && (
                  <ReceiptDetailInlineMobile
                    token={token}
                    password={password}
                    receiptId={tx.receiptId}
                  />
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </>
  );
};

export default TransactionHistoryMobile;

